<template>
  <div :id="rootID()" class="metafields-edit-container" :class="className">
    <BaseAccordion
      v-for="(field, i) in metafields"
      :id="field.key"
      :key="field.id" 
      :class="{ 'metafields-edit-container__panel': root }"
      :value="field.subForm && field.subForm.opened"
    >
      <template slot-scope="BaseAccordion1st">
        <component
          class="metafields-edit-container__field"
          :field="field" 
          :name="field.key"
          :groupName="field.label"
          :hint="field.description"
          :warning="generateWarning(field)"
          v-model="field.value"
          :subFormData="field.subForm"
          :is="basedOnMetafieldType(field.type)"
          @click.native="BaseAccordion1st.onCollapse"
          @valueChanged="setFieldChanged($event, field)"
          >
          <button
            slot="toggleBtn"
            class="metafields-edit-container__toggle"
            :class="{ 'metafields-edit-container__toggle--opened': BaseAccordion1st.opened }"
            >
            <BaseIcon
              v-if="field.children && (field.children.length || field.type === 'repeater' || field.type === 'group')"
              name="arrow"
            ></BaseIcon>
          </button>
        </component>

        <!-- CHILDREN -->
        <div
          class="metafields-edit-container__children"
          :class="{ '-INVISIBLE': !BaseAccordion1st.opened }"
          v-if="field.children && (field.children.length || field.type === 'repeater')"
          >

          <!-- REPEATER children -->
          <DraggableTree
            ref="repeaterTree"
            v-if="field.type === 'repeater'"
            :data="field.children"
            :options="{ handle: '.js-drag-handle' }"
            :indent="30"
            :crossTree="false"
            draggable
            :space="0"
            :getTriggerEl="dragHandler"
            :ondragend="onDragEnd"
            @drag="dragging = true"
            @drop="dragging = false"
            @moving="onDragMoving"
            @change="setRepeaterReorganized(true, field)"
            >
            <div slot-scope="TreeScope">
              <template v-if="!TreeScope.data.isDragPlaceHolder">
                <BaseAccordion
                  class="metafields-edit-container__children__repeater-group"
                  >
                  <template slot-scope="BaseAccordion2nd">
                    <MetafieldEditGroup
                      class="metafields-edit-container__field"
                      :groupNameOnceRender="!repeaterLabelByField(field, TreeScope.data).willBeLabeled"
                      :groupName="repeaterLabelByField(field, TreeScope.data).label ? repeaterLabelByField(field, TreeScope.data).label : repeaterGroupName(field, TreeScope.index + 1, TreeScope.data)"
                      @click.native="BaseAccordion2nd.onCollapse"
                      >
                      <div slot="dragHandle" class="js-drag-handle" @click.stop>
                        <div class="drag-handle">
                          <BaseIcon name="drag"/>
                        </div>
                      </div>
                      <button
                        slot="toggleBtn"
                        class="metafields-edit-container__toggle"
                        :class="{ 'metafields-edit-container__toggle--opened': BaseAccordion2nd.opened }"
                        >
                        <BaseIcon
                          v-if="field.children && (field.children.length || field.type === 'repeater')"
                          name="arrow"
                        ></BaseIcon>
                      </button>
                      <BaseButton
                        class="remove-group-button"
                        slot="removeBtn"
                        icon="trash"
                        :useImgNotSvg="true"
                        @click.native.stop="openConfirmDeleteModal($event, { repeater: field, groupIndex: TreeScope.index })"
                      ></BaseButton>
                    </MetafieldEditGroup>
                    <div class="metafields-edit-container__children" :class="{ '-INVISIBLE': !BaseAccordion2nd.opened }">
                      <MetafieldsEditContainer
                        :parentTheRepeater="field.type === 'repeater' ? field : null"
                        :groupTheRepeaterContainer="parentTheGroup"
                        :root="false"
                        :metafields="TreeScope.data"
                      ></MetafieldsEditContainer>
                    </div>
                  </template>
                </BaseAccordion>
              </template>
            </div>
          </DraggableTree>
          <NewFieldButton
            class="repeater-new-instance-btn"
            :data="field"
            :title="`Add a`"
            :titleStrong="field.subForm.title || 'Item'"
            v-if="field.type === 'repeater' && field.schema && field.schema.length"
            @click.native="handleAddGroupRepeater(field)"
          ></NewFieldButton>

          <!-- GROUP children -->
          <MetafieldsEditContainer
            v-else
            :root="false"
            :metafields="field.children"
            :parentTheGroup="field.type === 'group' ? field : null"
          ></MetafieldsEditContainer>
        </div>
      </template>
    </BaseAccordion>
  </div>
</template>

<script>
  import MetaFields from './metafields'
  import NewFieldButton from '@/components/NewFieldButton'
  import { DraggableTree } from 'vue-draggable-nested-tree'
  import { upperFirst, camelCase } from 'lodash'
  import { forMetafieldsContainer } from '@/mixins/metafields-changed-detection.mixin'
  import { MDSModalHintModel, MDSModalConfirmDecisionModel } from '@/models/modal-datasets.model'
  import { FIELD_TYPES_ALLOWED_FOR_REPEATER_LABEL } from '@/components/forms/field-types'

  export default {
    inject: ['$validator'],
    name: 'MetafieldsEditContainer',
    mixins: [ forMetafieldsContainer() ],
    components: {
      ...MetaFields,
      NewFieldButton,
      DraggableTree
    },
    props: {
      root: { type: Boolean, default: true },
      metafields: {
        type: Array,
        required: true
      },
      loading: Boolean
    },
    data () {
      return {
        blockDrop: false,
        dragging: false
      }
    },
    computed: {
      className () {
        return {
          'metafields-edit-container--drop-blocked': this.blockDrop,
          'metafields-edit-container--dragging': this.dragging,
          'metafields-edit-container--loading': this.loading
        }
      }
    },
    methods: {
      generateWarning (field) {
        if (field.type === 'repeater' && !field.schema.length) {
          return 'Repeater has no fields.'
        } else if (field.type === 'group' && !field.children.length) {
          return 'Group is empty.'
        }
      },
      rootID () {
        return this.root ? 'js-metafields-ROOT' : ''
      },
      dragHandler (nodeVm) {
        return nodeVm.$el.firstChild.querySelector('.js-drag-handle')
      },
      draggableRulesTest ({ draggingField, destinationNode, trueCallback, falseCallback }) {

        if (!destinationNode.isRoot) {
          return falseCallback()
        }

        else if (trueCallback) {
          trueCallback()
        }

        return true
      },
      onDragMoving () {
        const destinationNode = this.$refs.repeaterTree[0].dplh.parent

        this.draggableRulesTest({
          destinationNode,
          trueCallback: () => {
            this.blockDrop = false
          },
          falseCallback: () => {
            this.blockDrop = true
          }
        })
      },
      onDragEnd () {
        this.dragging = false
        const destinationNode = this.$refs.repeaterTree[0].dplh.parent

        const test = this.draggableRulesTest({
          destinationNode,
          falseCallback: () => {
            return false
          }
        })

        return test
      },
      basedOnMetafieldType (type) {
        return 'MetafieldEdit' + upperFirst(camelCase(type))
      },
      repeaterLabelByField(field, data) {
        const { labelFieldKey } = field.subForm
        const output = { willBeLabeled: false, label: '' }

        if (labelFieldKey) {
          output.willBeLabeled = true
          const labelField = data.find(item => labelFieldKey === item.key.substr(0, item.key.lastIndexOf('_')))

          if (labelField && FIELD_TYPES_ALLOWED_FOR_REPEATER_LABEL.includes(labelField.type)) {
            output.label = labelField.value ? String(labelField.value) : ''
          }
        }

        return output
      },
      repeaterGroupName (field, i) {
        return `${field.subForm.title || 'Item'} ${i}`
      },

      handleAddGroupRepeater (repeater) {
        if (repeater.subForm.max != 0 && repeater.children.length == repeater.subForm.max) {
          const props = new MDSModalHintModel({
            message: 'You reached maximum repeater instances specified in your schema.'
          })

          this.$store.commit('modals/OPEN_MODAL', { name: 'ModalHint', props })

          return
        }

        let _repeater = Object.assign({}, repeater)
        let _newGroup = [ ..._repeater.schema ]

        _newGroup = _newGroup.map((item, i) => {
          let _item = Object.assign({}, item)
          _item.key = `${_item.key}_[i]` + '__' + repeater.children.length

          return _item
        })

        repeater.children.push(_newGroup)
        
        this.setRepeaterReorganized(true, repeater)
      },

      handleRemoveGroupRepeater({repeater, groupIndex}) {
        if (repeater.children.length == repeater.subForm.min) {
          console.warn('You reached MIN amount of instances specified in field management.')
          return
        }

        let _children = repeater.children.slice()
        _children.splice(groupIndex, 1)

        repeater.children = _children

        this.setRepeaterReorganized(true, repeater)
      },

      openConfirmDeleteModal (e, {repeater, groupIndex}) {
        e.target.blur()
        e.target.parentNode.blur()

        let props, name

        if (repeater.children.length == repeater.subForm.min) {
          props = new MDSModalHintModel({
            message: 'You reached minimum repeater instances specified in your schema.'
          })
          name = 'ModalHint'
        } else {
          props = new MDSModalConfirmDecisionModel({
            confirmationHandler: () => this.handleRemoveGroupRepeater({repeater, groupIndex})
          })
          name = 'ModalConfirmDecision'
        }

        this.$store.commit('modals/OPEN_MODAL', { name, props })
      }
    }
  }
</script>

<style lang="scss">
  @import './src/assets/styles/colors.scss';
  @import './src/assets/styles/breakpoints.scss';
  @import './src/assets/styles/mixins.scss';

  .metafields-edit-container {
    width: 667px;
    margin-top: -1px;

    @include media(775px) {
      width: 100%;
    }

    &__panel {
      position: relative;
      background-color: $color-catskill-white;
      border-radius: 3px;
      box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);

      &:not(:last-of-type) {
        margin-bottom: 20px;
      }
    }

    .drag-handle {
      padding: 25px 10px 25px 20px;
      cursor: move;
      cursor: -webkit-grabbing; /* stylelint-disable-line */

      svg {
        width: 6px;
        height: 10px;
      }
    }

    .repeater-new-instance-btn {
      width: calc(100% + 32px);
      margin-left: -31px;
    }

    .metafields-edit-container {
      width: 100%;
      margin-bottom: -1px;
      margin-right: -1px;
      overflow: visible;
      box-shadow: none;
    }

    .base-input {
      margin-bottom: 0;
    }

    .metafield__title {
      @include base-field-title;
    }
    
    &__field {
      display: flex;
      align-items: center;
      min-height: 67px;
      padding: 20px;
      background-color: #fff;
      border: 1px solid $color-border;
      margin: -1px -1px 0 -1px;

      &:first-of-type {
        border-radius: 3px 3px 0 0;
      }

      &:last-of-type {
        border-radius: 0 0 3px 3px;
      }

      &:first-of-type:last-of-type {
        border-radius: 3px;
      }

      .base-input {
        flex-grow: 1;
      }
    }

    &__children {
      padding-left: 30px;
      
      &__repeater-group {
        position: relative;

        .remove-group-button {
          margin-left: auto;
          margin-right: 10px;
        }
      }
    }

    &__ico-cancel {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }

    &__toggle {
      position: relative;
      display: flex;
      padding: 30px 20px;
      user-select: none;

      svg {
        position: relative;
        width: 10px;
        height: 5px;
        transform: rotateZ(-90deg);
        transition: transform .3s;
        z-index: 1;
      }

      &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 40px;
        height: 35px;
        background-color: #ebf5fa;
        border-radius: 2px;
        opacity: 0;
        pointer-events: none;
        transform: translate(-50%, -50%);
        transition: opacity .15s;
      }

      &:hover {
        @include media(hover) {
          &:before {
            opacity: 1;
          }
        }
      }

      &--opened {
        svg {
          transform: none;
        }
      }
    }

    .draggable-placeholder-inner {
      position: relative;
      height: 0 !important;

      &:before, &:after {
        content: '';
        position: absolute;
        z-index: 10;
        transition-duration: .15s;
      }

      &:before {
        top: -1px;
        right: 0;
        bottom: -1px;
        left: 0;
        background-color: $color-purple;
        transition-property: background-color;
      }

      &:after {
        top: -7px;
        left: -2px;
        width: 10px;
        height: 10px;
        background-color: #fff;
        border: 2px solid $color-purple;
        border-radius: 50%;
        transition-property: border-color;
      }
    }

    .tree-node.dragging {
      width: auto !important;
      border-radius: 6px;
      opacity: .85 !important;

      .metafields-edit-container__children {
        display: none;
      }

      .remove-group-button {
        display: none;
      }
    }

    .-INVISIBLE {
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 0;
      opacity: 0;
      pointer-events: none;
      overflow: hidden;
    }

    &--drop-blocked {
      .draggable-placeholder-inner {
        &:before {
          background-color: $color-monza;
        }
        &:after {
          border-color: $color-monza;
        }
      }
    }

    &--dragging {
      user-select: none;

      .new-field-button:hover {
        background-color: inherit !important;
        color: inherit !important;
      }
    }

    &--loading {
      pointer-events: none;
      opacity: .5;

      * {
        pointer-events: none;
      }
    }
  }
</style>


// WEBPACK FOOTER //
// src/components/metafields-edit/MetafieldsEditContainer.vue